export class NotificationTracker {
    static STORAGE_KEYS = {
        SCHEDULED: 'notification_tracker_scheduled',
        STATS: 'notification_tracker_stats',
        HISTORY: 'notification_tracker_history',
        DEBUG: 'notification_tracker_debug'
    };
    static MAX_HISTORY_ENTRIES = 100;
    /**
     * Track a newly scheduled notification
     */
    static trackScheduled(notification) {
        try {
            const scheduledInfo = {
                ...notification,
                status: 'scheduled',
                scheduledAt: new Date().toISOString()
            };
            // Add to scheduled notifications
            const scheduled = this.getScheduledNotifications();
            scheduled[notification.id] = scheduledInfo;
            this.saveScheduledNotifications(scheduled);
            // Update stats
            this.updateStats('scheduled', scheduledInfo);
            // Add to history
            this.addToHistory(scheduledInfo);
            console.log(`[NotificationTracker] Tracked scheduled notification: ${notification.title}`);
        }
        catch (error) {
            console.error('[NotificationTracker] Failed to track scheduled notification:', error);
        }
    }
    /**
     * Mark a notification as sent
     */
    static trackSent(notificationId) {
        try {
            const scheduled = this.getScheduledNotifications();
            const notification = scheduled[notificationId];
            if (notification) {
                notification.status = 'sent';
                this.saveScheduledNotifications(scheduled);
                this.updateStats('sent', notification);
                this.addToHistory(notification);
                console.log(`[NotificationTracker] Tracked sent notification: ${notification.title}`);
            }
        }
        catch (error) {
            console.error('[NotificationTracker] Failed to track sent notification:', error);
        }
    }
    /**
     * Mark a notification as cancelled
     */
    static trackCancelled(notificationId) {
        try {
            const scheduled = this.getScheduledNotifications();
            const notification = scheduled[notificationId];
            if (notification) {
                notification.status = 'cancelled';
                this.saveScheduledNotifications(scheduled);
                this.updateStats('cancelled', notification);
                this.addToHistory(notification);
                console.log(`[NotificationTracker] Tracked cancelled notification: ${notification.title}`);
            }
        }
        catch (error) {
            console.error('[NotificationTracker] Failed to track cancelled notification:', error);
        }
    }
    /**
     * Clean up expired notifications
     */
    static cleanupExpired() {
        try {
            const scheduled = this.getScheduledNotifications();
            const now = new Date();
            let expiredCount = 0;
            Object.keys(scheduled).forEach(id => {
                const notification = scheduled[id];
                const scheduledTime = new Date(notification.scheduledFor);
                // Mark as expired if scheduled time is more than 1 hour in the past
                if (scheduledTime.getTime() < now.getTime() - 60 * 60 * 1000) {
                    notification.status = 'expired';
                    this.updateStats('expired', notification);
                    this.addToHistory(notification);
                    expiredCount++;
                }
            });
            if (expiredCount > 0) {
                this.saveScheduledNotifications(scheduled);
                console.log(`[NotificationTracker] Cleaned up ${expiredCount} expired notifications`);
            }
        }
        catch (error) {
            console.error('[NotificationTracker] Failed to cleanup expired notifications:', error);
        }
    }
    /**
     * Get all scheduled notifications
     */
    static getScheduledNotifications() {
        try {
            const data = localStorage.getItem(this.STORAGE_KEYS.SCHEDULED);
            return data ? JSON.parse(data) : {};
        }
        catch (error) {
            console.error('[NotificationTracker] Failed to get scheduled notifications:', error);
            return {};
        }
    }
    /**
     * Get notification statistics
     */
    static getStats() {
        try {
            const data = localStorage.getItem(this.STORAGE_KEYS.STATS);
            return data ? JSON.parse(data) : {
                totalScheduled: 0,
                totalSent: 0,
                totalCancelled: 0,
                totalExpired: 0,
                byChallenge: {},
                byPlatform: {},
                lastScheduled: null,
                lastSent: null
            };
        }
        catch (error) {
            console.error('[NotificationTracker] Failed to get stats:', error);
            return {
                totalScheduled: 0,
                totalSent: 0,
                totalCancelled: 0,
                totalExpired: 0,
                byChallenge: {},
                byPlatform: {},
                lastScheduled: null,
                lastSent: null
            };
        }
    }
    /**
     * Get notification history
     */
    static getHistory() {
        try {
            const data = localStorage.getItem(this.STORAGE_KEYS.HISTORY);
            return data ? JSON.parse(data) : [];
        }
        catch (error) {
            console.error('[NotificationTracker] Failed to get history:', error);
            return [];
        }
    }
    /**
     * Clear all tracking data
     */
    static clearAll() {
        try {
            Object.values(this.STORAGE_KEYS).forEach(key => {
                localStorage.removeItem(key);
            });
            console.log('[NotificationTracker] Cleared all tracking data');
        }
        catch (error) {
            console.error('[NotificationTracker] Failed to clear tracking data:', error);
        }
    }
    /**
     * Get debug information
     */
    static getDebugInfo() {
        return {
            scheduled: this.getScheduledNotifications(),
            stats: this.getStats(),
            history: this.getHistory(),
            localStorage: {
                notificationsEnabled: localStorage.getItem('notifications-enabled'),
                permissionAsked: localStorage.getItem('notification-permission-asked'),
                lastScheduled: localStorage.getItem('notifications-last-scheduled'),
                webPending: localStorage.getItem('web_notification_wrapper_pending'),
                webScheduled: localStorage.getItem('web_notification_wrapper_scheduled')
            }
        };
    }
    // Private helper methods
    static saveScheduledNotifications(scheduled) {
        try {
            localStorage.setItem(this.STORAGE_KEYS.SCHEDULED, JSON.stringify(scheduled));
        }
        catch (error) {
            console.error('[NotificationTracker] Failed to save scheduled notifications:', error);
        }
    }
    static updateStats(action, notification) {
        try {
            const stats = this.getStats();
            const now = new Date().toISOString();
            // Update counters
            switch (action) {
                case 'scheduled':
                    stats.totalScheduled++;
                    stats.lastScheduled = now;
                    break;
                case 'sent':
                    stats.totalSent++;
                    stats.lastSent = now;
                    break;
                case 'cancelled':
                    stats.totalCancelled++;
                    break;
                case 'expired':
                    stats.totalExpired++;
                    break;
            }
            // Update by challenge
            if (notification.challengeId) {
                stats.byChallenge[notification.challengeId] = (stats.byChallenge[notification.challengeId] || 0) + 1;
            }
            // Update by platform
            stats.byPlatform[notification.platform] = (stats.byPlatform[notification.platform] || 0) + 1;
            localStorage.setItem(this.STORAGE_KEYS.STATS, JSON.stringify(stats));
        }
        catch (error) {
            console.error('[NotificationTracker] Failed to update stats:', error);
        }
    }
    static addToHistory(notification) {
        try {
            const history = this.getHistory();
            history.unshift(notification);
            // Keep only the latest entries
            if (history.length > this.MAX_HISTORY_ENTRIES) {
                history.splice(this.MAX_HISTORY_ENTRIES);
            }
            localStorage.setItem(this.STORAGE_KEYS.HISTORY, JSON.stringify(history));
        }
        catch (error) {
            console.error('[NotificationTracker] Failed to add to history:', error);
        }
    }
}
